import React, { createContext, useContext, useMemo, useRef, useState } from 'react'

export type DebugSource = 'bootstrap' | 'ws' | 'query' | 'system'
export type DebugLevel = 'info' | 'warn' | 'error'

export type DebugEvent = {
  id: string
  ts: number
  source: DebugSource
  level: DebugLevel
  label: string
  meta?: Record<string, any>
}

type Ctx = {
  enabled: boolean
  addLog: (_e: Omit<DebugEvent, 'id' | 'ts'>) => void
  clear: () => void
  events: DebugEvent[]
}

const DebugLogCtx = createContext<Ctx | null>(null)

const MAX_EVENTS = 500

export function DebugLogProvider({ children, enabled = false }: { children: React.ReactNode; enabled?: boolean }) {
  const [events, setEvents] = useState<DebugEvent[]>([])
  const queueRef = useRef<DebugEvent[]>([])
  const rafRef = useRef<number | null>(null)
  const lastRef = useRef<DebugEvent | null>(null)

  const flush = () => {
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      if (queueRef.current.length) {
        setEvents(prev => {
          const next = [...queueRef.current.reverse(), ...prev].slice(0, MAX_EVENTS)
          return next
        })
        queueRef.current = []
      }
      rafRef.current = null
    })
  }

  const addLog: Ctx['addLog'] = e => {
    if (!enabled) return
    const now = Date.now()
    const last = lastRef.current
    if (last && last.source === e.source && last.label === e.label && now - last.ts < 500) {
      return // drop near-duplicate
    }
    const event = { ...e, id: crypto.randomUUID(), ts: now }
    lastRef.current = event
    queueRef.current.push(event)
    flush()
  }

  const clear = () => setEvents([])

  const value = useMemo<Ctx>(() => ({ enabled, addLog, clear, events }), [enabled, events])

  return <DebugLogCtx.Provider value={value}>{children}</DebugLogCtx.Provider>
}

export function useDebugLog() {
  const ctx = useContext(DebugLogCtx)
  if (!ctx) {
    // no-op shim so you can keep calls in code when disabled
    return {
      enabled: false,
      addLog: (_e: Omit<DebugEvent, 'id' | 'ts'>) => {},
      clear: () => {},
      events: [] as DebugEvent[],
    }
  }
  return ctx
}
